import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getSiteApi } from '../services/apiCalls';

import { websiteData } from '../data/data';

/* ─────────────────────────────────────────
   CONTEXT
───────────────────────────────────────── */
const SiteContext = createContext();
export const useSite = () => useContext(SiteContext);

/* ─────────────────────────────────────────
   FALLBACK / INITIAL DATA
   Note: `loading` lives in component state,
   NOT inside this object.
───────────────────────────────────────── */
const FALLBACKS = {
  admin: [
    {
      id: 'a1',
      fullName: 'Super Admin',
      email: 'admin@wayapaychat.com',
      password: 'admin@wayapaychat.com',
      isProtected: true,
    },
    {
      id: 'a2',
      fullName: 'Samuel Muoto',
      email: typeof process !== 'undefined' ? process.env.EMAIL_ADDRESS : '',
      password: typeof process !== 'undefined' ? process.env.PASSWORD : '',
      isProtected: true,
    },
  ],
  ...websiteData
};

/* ─────────────────────────────────────────
   PROVIDER
───────────────────────────────────────── */
export const SiteProvider = ({ children }) => {
  const [siteData, setSiteData] = useState(FALLBACKS);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchAllData = async () => {
      try {
        const results = await Promise.allSettled([
          getSiteApi('settings'),           // 0
          getSiteApi('about/contents'),     // 1
          getSiteApi('home_page'),          // 2
          getSiteApi('blogs'),              // 3
          getSiteApi('features'),           // 4
          getSiteApi('how_it_works'),       // 5
          getSiteApi('faqs'),              // 6 — dedicated endpoint
          getSiteApi('testimonials'),       // 7 — dedicated endpoint
          getSiteApi('partners'),          // 8 — dedicated endpoint
          getSiteApi('ecosystem'),         // 9 — dedicated endpoint
          getSiteApi('advantages'),        // 10 — dedicated endpoint
          getSiteApi('about/intro'),       // 11 — dedicated endpoint
        ]);

        if (!mounted) return;

        const [
          settingsRes,
          aboutRes,
          homeRes,
          blogsRes,
          featuresRes,
          howItWorksRes,
          faqsRes,
          testimonialsRes,
          partnersRes,
          ecosystemRes,
          advantagesRes,
          aboutIntroRes,
        ] = results;

        /* Helper: safely extract fulfilled value or fall back to prev */
        const ok = (res) =>
          res.status === 'fulfilled' && res.value?.data ? res.value.data : null;

        setSiteData((prev) => ({
          ...prev,
          settings:       ok(settingsRes)      || prev.settings,
          aboutIntro:     ok(aboutIntroRes)    || prev.aboutIntro,
          aboutContent:   Array.isArray(ok(aboutRes))        ? ok(aboutRes)        : prev.aboutContent,
          homeContent:    ok(homeRes)          || prev.homeContent,
          blogs:          Array.isArray(ok(blogsRes))         ? ok(blogsRes)         : prev.blogs,
          features:       Array.isArray(ok(featuresRes))      ? ok(featuresRes)      : prev.features,
          howItWorks:     ok(howItWorksRes)    || prev.howItWorks,
          faqs:           ok(faqsRes)          || prev.faqs,
          testimonials:   Array.isArray(ok(testimonialsRes))  ? ok(testimonialsRes)  : prev.testimonials,
          partners:       Array.isArray(ok(partnersRes))      ? ok(partnersRes)      : prev.partners,
          ecosystem:      Array.isArray(ok(ecosystemRes))     ? ok(ecosystemRes)     : prev.ecosystem,
          advantages:     Array.isArray(ok(advantagesRes))    ? ok(advantagesRes)    : prev.advantages,
          // bankingDetails and ctaSections come from homeRes if the API bundles them
          bankingDetails: ok(homeRes)?.bankingDetails || prev.bankingDetails,
          ctaSections:    ok(homeRes)?.ctaSections    || prev.ctaSections,
        }));
      } catch (error) {
        if (mounted) console.error('SiteContext fetch error:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAllData();
    return () => { mounted = false; };
  }, []);

  /* Memoize so consumers don't re-render on unrelated state changes */
  const value = useMemo(
    () => ({ ...siteData, loading }),
    [siteData, loading]
  );

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};
